import React, { useEffect } from 'react';
import { ArrowLeft, CreditCard, Shield, Lock, Database, Network, FileCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const PCIDSSPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="dark-container">
      <Header />
      
      {/* Hero Section */}
      <section className="service-hero">
        <div className="dark-content-container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          
          <div className="service-hero-content">
            <div className="service-badge">
              <CreditCard className="service-badge-icon" />
              <span>PCI DSS Audit & Compliance Support</span>
            </div>
            
            <h1 className="service-title">
              Payment Card Industry 
              <span className="service-title-accent"> Data Security Standard</span>
            </h1>
            
            <p className="service-description">
              Complete PCI DSS compliance assessment and ongoing support services to protect 
              cardholder data and maintain certification. Ensure your payment processing 
              environment meets all security requirements.
            </p>
            
            <div className="service-stats">
              <div className="service-stat">
                <span className="service-stat-number">100%</span>
                <span className="service-stat-label">Compliance Achievement Rate</span>
              </div>
              <div className="service-stat">
                <span className="service-stat-number">$4.2M</span>
                <span className="service-stat-label">Average Breach Cost Avoided</span>
              </div>
              <div className="service-stat">
                <span className="service-stat-number">24/7</span>
                <span className="service-stat-label">Monitoring & Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PCI DSS Requirements */}
      <section className="pci-requirements-section">
        <div className="dark-content-container">
          <h2 className="display-large">12 PCI DSS Requirements</h2>
          
          <div className="requirements-grid">
            <div className="requirement-group">
              <h3 className="group-title">Build & Maintain Secure Network</h3>
              <div className="requirement-cards">
                <div className="requirement-card">
                  <div className="req-number">1</div>
                  <div className="req-content">
                    <h4 className="req-title">Firewall Configuration</h4>
                    <p className="req-description">Install and maintain firewall configuration to protect cardholder data</p>
                  </div>
                </div>
                <div className="requirement-card">
                  <div className="req-number">2</div>
                  <div className="req-content">
                    <h4 className="req-title">System Passwords</h4>
                    <p className="req-description">Do not use vendor-supplied defaults for system passwords and security parameters</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="requirement-group">
              <h3 className="group-title">Protect Cardholder Data</h3>
              <div className="requirement-cards">
                <div className="requirement-card">
                  <div className="req-number">3</div>
                  <div className="req-content">
                    <h4 className="req-title">Data Protection</h4>
                    <p className="req-description">Protect stored cardholder data through encryption and data retention policies</p>
                  </div>
                </div>
                <div className="requirement-card">
                  <div className="req-number">4</div>
                  <div className="req-content">
                    <h4 className="req-title">Encryption in Transit</h4>
                    <p className="req-description">Encrypt transmission of cardholder data across open, public networks</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="requirement-group">
              <h3 className="group-title">Maintain Vulnerability Management</h3>
              <div className="requirement-cards">
                <div className="requirement-card">
                  <div className="req-number">5</div>
                  <div className="req-content">
                    <h4 className="req-title">Anti-Malware</h4>
                    <p className="req-description">Protect systems against malware and regularly update anti-virus software</p>
                  </div>
                </div>
                <div className="requirement-card">
                  <div className="req-number">6</div>
                  <div className="req-content">
                    <h4 className="req-title">Secure Development</h4>
                    <p className="req-description">Develop and maintain secure systems and applications</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="requirement-group">
              <h3 className="group-title">Implement Strong Access Controls</h3>
              <div className="requirement-cards">
                <div className="requirement-card">
                  <div className="req-number">7</div>
                  <div className="req-content">
                    <h4 className="req-title">Need-to-Know Access</h4>
                    <p className="req-description">Restrict access to cardholder data by business need-to-know</p>
                  </div>
                </div>
                <div className="requirement-card">
                  <div className="req-number">8</div>
                  <div className="req-content">
                    <h4 className="req-title">User Authentication</h4>
                    <p className="req-description">Identify and authenticate access to system components</p>
                  </div>
                </div>
                <div className="requirement-card">
                  <div className="req-number">9</div>
                  <div className="req-content">
                    <h4 className="req-title">Physical Access</h4>
                    <p className="req-description">Restrict physical access to cardholder data</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="requirement-group">
              <h3 className="group-title">Monitor & Test Networks</h3>
              <div className="requirement-cards">
                <div className="requirement-card">
                  <div className="req-number">10</div>
                  <div className="req-content">
                    <h4 className="req-title">Logging & Monitoring</h4>
                    <p className="req-description">Track and monitor access to network resources and cardholder data</p>
                  </div>
                </div>
                <div className="requirement-card">
                  <div className="req-number">11</div>
                  <div className="req-content">
                    <h4 className="req-title">Security Testing</h4>
                    <p className="req-description">Regularly test security systems and processes</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="requirement-group">
              <h3 className="group-title">Maintain Information Security Policy</h3>
              <div className="requirement-cards">
                <div className="requirement-card">
                  <div className="req-number">12</div>
                  <div className="req-content">
                    <h4 className="req-title">Security Policy</h4>
                    <p className="req-description">Maintain policy that addresses information security for all personnel</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Levels */}
      <section className="compliance-levels-section">
        <div className="dark-content-container">
          <h2 className="display-large">PCI DSS Merchant Levels & Validation</h2>
          
          <div className="levels-grid">
            <div className="level-card level-1-card">
              <div className="level-header">
                <h3 className="level-title">Level 1</h3>
                <span className="level-volume">6M+ Transactions/Year</span>
              </div>
              <div className="level-requirements">
                <p className="level-description">Highest level merchants requiring annual on-site assessment</p>
                <ul className="level-list">
                  <li>Annual Report on Compliance (ROC)</li>
                  <li>Quarterly vulnerability scans</li>
                  <li>On-site security assessment</li>
                  <li>Annual penetration testing</li>
                </ul>
              </div>
            </div>

            <div className="level-card level-2-card">
              <div className="level-header">
                <h3 className="level-title">Level 2</h3>
                <span className="level-volume">1M-6M Transactions/Year</span>
              </div>
              <div className="level-requirements">
                <p className="level-description">Mid-volume merchants with annual self-assessment</p>
                <ul className="level-list">
                  <li>Annual Self-Assessment Questionnaire (SAQ)</li>
                  <li>Quarterly vulnerability scans</li>
                  <li>Annual penetration testing</li>
                  <li>Network segmentation validation</li>
                </ul>
              </div>
            </div>

            <div className="level-card level-3-card">
              <div className="level-header">
                <h3 className="level-title">Level 3</h3>
                <span className="level-volume">20K-1M Transactions/Year</span>
              </div>
              <div className="level-requirements">
                <p className="level-description">Mid-volume e-commerce merchants</p>
                <ul className="level-list">
                  <li>Annual Self-Assessment Questionnaire (SAQ)</li>
                  <li>Quarterly vulnerability scans</li>
                  <li>Annual compliance validation</li>
                  <li>Risk assessment updates</li>
                </ul>
              </div>
            </div>

            <div className="level-card level-4-card">
              <div className="level-header">
                <h3 className="level-title">Level 4</h3>
                <span className="level-volume">Under 20K Transactions/Year</span>
              </div>
              <div className="level-requirements">
                <p className="level-description">Smallest merchants with simplified requirements</p>
                <ul className="level-list">
                  <li>Annual Self-Assessment Questionnaire (SAQ)</li>
                  <li>Quarterly vulnerability scans (if applicable)</li>
                  <li>Compliance validation</li>
                  <li>Basic security awareness</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Assessment Process */}
      <section className="methodology-section">
        <div className="dark-content-container">
          <h2 className="display-large">PCI DSS Assessment Process</h2>
          
          <div className="methodology-grid">
            <div className="methodology-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3 className="heading-2">Scoping & Segmentation</h3>
                <p className="body-medium">
                  Accurate scope definition and network segmentation validation to minimize 
                  PCI DSS assessment scope and reduce compliance burden.
                </p>
                <ul className="step-features">
                  <li>Cardholder data environment mapping</li>
                  <li>Network segmentation validation</li>
                  <li>Data flow analysis</li>
                  <li>Connected system identification</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3 className="heading-2">Gap Analysis & Remediation</h3>
                <p className="body-medium">
                  Comprehensive assessment against all 12 PCI DSS requirements with 
                  detailed remediation planning and implementation support.
                </p>
                <ul className="step-features">
                  <li>Requirements compliance assessment</li>
                  <li>Control testing and validation</li>
                  <li>Gap remediation planning</li>
                  <li>Implementation guidance</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3 className="heading-2">Security Testing</h3>
                <p className="body-medium">
                  Comprehensive security testing including vulnerability scans, 
                  penetration testing, and security control validation.
                </p>
                <ul className="step-features">
                  <li>Quarterly vulnerability scanning</li>
                  <li>Annual penetration testing</li>
                  <li>Wireless network testing</li>
                  <li>Application security testing</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">04</div>
              <div className="step-content">
                <h3 className="heading-2">Validation & Reporting</h3>
                <p className="body-medium">
                  Final compliance validation, documentation preparation, and ongoing 
                  compliance monitoring to maintain certification status.
                </p>
                <ul className="step-features">
                  <li>ROC/SAQ completion and submission</li>
                  <li>Compliance documentation</li>
                  <li>Ongoing monitoring setup</li>
                  <li>Annual reassessment planning</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PCIDSSPage;